/**
 * Step 1 — section registry.
 *
 * Defines the rendering order of the 12 backend item-definition
 * sections + the v2-only LinkedSubTaras hierarchical-mode block.
 * Step1ItemDefinition iterates this list instead of hardcoding each
 * `<Section data={...} />` call. Adding a new section is one new
 * file + one registry entry.
 *
 * Three sub-types:
 *   - **data-driven** — pulls `data[key]` and passes via `data` prop.
 *     The standard pattern for 11 of the 12 backend sections.
 *   - **custom-prop** — needs a different prop name (e.g. items vs
 *     data) or a different data source (raw agent output vs
 *     structured response). Used by LinkedSubTaras.
 *   - **read-only** — never participates in edit mode. ISO 21434
 *     Compliance lives here because its summary fields are computed
 *     server-side and don't round-trip coherently.
 */
import React from 'react';
import CoreInformation from './CoreInformation';
import SystemArchitecture from './SystemArchitecture';
import LifecycleFunctions from './LifecycleFunctions';
import InterfacesConnectivity from './InterfacesConnectivity';
import OperatingEnvironment from './OperatingEnvironment';
import SecurityFeatures from './SecurityFeatures';
import UpdateMaintenance from './UpdateMaintenance';
import SupplyChain from './SupplyChain';
import AttackSurface from './AttackSurface';
import ComplianceStandards from './ComplianceStandards';
import AssumptionsIssues from './AssumptionsIssues';
import Iso21434Compliance from './Iso21434Compliance';
import ExtractionQuality from './ExtractionQuality';
import LinkedSubTaras from './LinkedSubTaras';
import type { OnChangeField } from './parts';

/** Shared context every section descriptor's `render` receives. */
export interface SectionRenderContext {
  /** The structured item-definition payload (or its edit-mode draft). */
  data: any;
  /** Raw analysis_agent output — has `imported_child_taras` for hierarchical mode. */
  rawAgent: any;
  isEditing: boolean;
  onChange: OnChangeField;
}

export interface SectionDescriptor {
  /** Stable key used as React `key` + as an anchor id reference. */
  key: string;
  /** Human label rendered in the tab nav. */
  label: string;
  /** Returns the JSX for this section given the render context. */
  render: (ctx: SectionRenderContext) => React.ReactNode;
  /** Optional visibility predicate. When omitted, the section always renders. */
  visible?: (ctx: SectionRenderContext) => boolean;
}

/**
 * Ordered list of Step 1 sections. The order is intentional and
 * matches the legacy CompleteItemDefinition tab order, with
 * LinkedSubTaras slotted in after Core Information (hierarchical-
 * mode-only).
 */
export const STEP1_SECTIONS: SectionDescriptor[] = [
  {
    key: 'core_information',
    label: 'Core',
    render: ({ data, isEditing, onChange }) => (
      <CoreInformation data={data?.core_information} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'imported_child_taras',
    label: 'Linked Sub-TARAs',
    visible: ({ rawAgent }) => {
      const items = rawAgent?.imported_child_taras ?? rawAgent?.result?.imported_child_taras;
      return Array.isArray(items) && items.length > 0;
    },
    render: ({ rawAgent, isEditing }) => {
      const items = rawAgent?.imported_child_taras ?? rawAgent?.result?.imported_child_taras;
      return <LinkedSubTaras items={items} isEditing={isEditing} />;
    },
  },
  {
    key: 'system_architecture',
    label: 'Architecture',
    render: ({ data, isEditing, onChange }) => (
      <SystemArchitecture data={data?.system_architecture} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'lifecycle_and_functions',
    label: 'Lifecycle & Functions',
    render: ({ data, isEditing, onChange }) => (
      <LifecycleFunctions data={data?.lifecycle_and_functions} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'interfaces_and_connectivity',
    label: 'Interfaces',
    render: ({ data, isEditing, onChange }) => (
      <InterfacesConnectivity data={data?.interfaces_and_connectivity} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'operating_environment',
    label: 'Environment',
    render: ({ data, isEditing, onChange }) => (
      <OperatingEnvironment data={data?.operating_environment} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'security_features',
    label: 'Security',
    render: ({ data, isEditing, onChange }) => (
      <SecurityFeatures data={data?.security_features} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'update_and_maintenance',
    label: 'Update & Maintenance',
    render: ({ data, isEditing, onChange }) => (
      <UpdateMaintenance data={data?.update_and_maintenance} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'supply_chain',
    label: 'Supply Chain',
    render: ({ data, isEditing, onChange }) => (
      <SupplyChain data={data?.supply_chain} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'attack_surface',
    label: 'Attack Surface',
    render: ({ data, isEditing, onChange }) => (
      <AttackSurface data={data?.attack_surface} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'compliance_and_standards',
    label: 'Compliance',
    render: ({ data, isEditing, onChange }) => (
      <ComplianceStandards data={data?.compliance_and_standards} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'assumptions_and_issues',
    label: 'Assumptions & Issues',
    render: ({ data, isEditing, onChange }) => (
      <AssumptionsIssues data={data?.assumptions_and_issues} isEditing={isEditing} onChange={onChange} />
    ),
  },
  {
    key: 'iso_21434_compliance',
    label: 'ISO 21434',
    render: ({ data }) => <Iso21434Compliance data={data?.iso_21434_compliance} />,
  },
  {
    key: 'extraction_quality',
    label: 'Extraction Quality',
    // Read-only: these are measurements of how the extraction went, not
    // fields a reviewer edits. Only rendered when the agent reported any —
    // an always-empty tab would be noise on hand-authored definitions.
    visible: ({ data }) => Boolean(data?.extraction_quality),
    render: ({ data }) => <ExtractionQuality data={data} />,
  },
];
